import { Button } from '@eventbrite/eds-button';
import { logEvent } from '@eventbrite/statsig';
import classNames from 'classnames';
import React from 'react';
import type { Event } from '../../../types/index';
import './ConversionBar.scss';

export type ConversionBarCommonProps = {
    statusIsUnavailable: boolean;
    shouldDisplayRsvp: boolean;
    shouldShowStatus: boolean;
    panelDisplayPrice: string;
    statusToDisplay: string;
    shouldShowCheckoutButton: boolean;
    getSeriesDefaultOptionTerminology: string;
    ticketModalButton: any;
    shouldShowRemindMeButton: boolean;
    shouldShowTurnOffReminderButton: boolean;
    isExpiredEvent: boolean;
};

export type ConversionBarProps = ConversionBarCommonProps & {
    externalTicketsUrl: string;
    externalTicketsButtonText: string;
    externalTicketsAreAvailable: boolean;
};

export type ConversionBarComponentProps = ConversionBarCommonProps & {
    externalTickets?: {
        //TODO: build on the backend
        url: string;
        text: string;
        available: boolean;
        shouldShowExternalTicketingProvider: boolean;
        externalTicketingProviderLabelText?: string;
    };
} & Pick<Event, 'id' | 'isSeriesParent'>;

export const ConversionBar = ({
    statusIsUnavailable,
    shouldDisplayRsvp,
    shouldShowStatus,
    statusToDisplay,
    panelDisplayPrice,
    externalTickets,
    ticketModalButton,
    id, //TODO: rename on picking
    shouldShowRemindMeButton,
    shouldShowCheckoutButton,
    isSeriesParent,
    isExpiredEvent,
}: ConversionBarComponentProps) => {
    const {
        shouldShowExternalTicketingProvider,
        externalTicketingProviderLabelText,
    } = externalTickets ?? {};

    const ExternalTickets = () => (
        <Button
            href={externalTickets?.url}
            type="link"
            style="fill"
            size="block"
            target="_blank"
            rel="noreferrer"
        >
            {externalTickets?.text}
        </Button>
    );

    const CheckoutArea = () => (
        <>
            {shouldShowRemindMeButton && (
                <div id="listings-root__remind-me"></div>
            )}
            <CheckoutButton />
        </>
    );

    const CheckoutButton: React.FC = ({ children }) => {
        return (
            <Button
                id={`eventbrite-widget-modal-trigger-${id}`}
                style={shouldShowStatus ? 'neutral' : 'fill'}
                data-tracking-label={ticketModalButton.tracking_label}
                __containerClassName={classNames({
                    'js-embed-ticket-modal-btn': !isSeriesParent,
                })}
                size="block"
                onClick={() =>
                    logEvent('Listing - conversionBar - Click - CTA')
                }
            >
                {children ? children : ticketModalButton.text}
            </Button>
        );
    };

    const SingleEvent = () => (
        <>
            {shouldDisplayRsvp ? (
                <div id="listings-root__rsvp"></div>
            ) : (
                <>
                    {shouldShowCheckoutButton && <CheckoutArea />}
                    {externalTickets?.available && <ExternalTickets />}
                </>
            )}
        </>
    );

    const PanelInfo = () => {
        const status = shouldShowStatus ? statusToDisplay : panelDisplayPrice;

        return (
            <div className="conversion-bar__panel-info">
                {shouldDisplayRsvp ? (
                    <div id="listings-root__rsvp-label"></div>
                ) : (
                    <>
                        {status}
                        {shouldShowExternalTicketingProvider && (
                            <>
                                {' '}
                                <span className="conversion-bar__additional-info">
                                    {externalTicketingProviderLabelText}
                                </span>
                            </>
                        )}
                    </>
                )}
            </div>
        );
    };

    const shouldShowCTA =
        !statusIsUnavailable &&
        (shouldShowCheckoutButton ||
            externalTickets?.available ||
            shouldDisplayRsvp);

    return (
        <div
            className={classNames(
                {
                    'eds-hide': isExpiredEvent,
                    'conversion-bar--no-cta': !shouldShowCTA,
                },
                'conversion-bar eds-fx--slide-up',
            )}
        >
            <div className="conversion-bar__body">
                <PanelInfo />
            </div>
            {shouldShowCTA && (
                <div className="conversion-bar__cta-wrapper eds-fx--fade-in eds-fx--delay-18">
                    <SingleEvent />
                </div>
            )}
        </div>
    );
};
